"use client";
import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { getUnlistedPriceData } from "@/app/api/service/unlistedstocks";

interface TradeData {
    tradeDate: string;
    price: number;
}

interface MappedData {
    x: number; // Unix timestamp in milliseconds
    y: number; // Price
}

interface UseUnlistedGraphDataProps {
    isin: string;
    period: "1_month" | "3_months" | "1_year" | "2_years" | "3_years" | "all";
    refetch: boolean;
}

interface UseUnlistedGraphDataReturn {
    filteredData: MappedData[];
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

const useUnlistedGraphData = ({
    isin,
    period,
    refetch,
}: UseUnlistedGraphDataProps): UseUnlistedGraphDataReturn => {
    const [filteredData, setFilteredData] = useState<MappedData[]>([]);
    const [catchData, setCatchedData] = useState<TradeData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let data: TradeData[];
            if (catchData.length) {
                data = catchData;
            } else {
                const response = await getUnlistedPriceData(isin);
                data = response.data;
                setCatchedData(data);
            }

            const filtered = filterDataByPeriod(data, period);
            const finalArray: MappedData[] = filtered.map((point) => ({
                x: moment(point.tradeDate, "MM/DD/YYYY").valueOf(),
                y: point.price,
            }));
            setFilteredData(finalArray);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [isin, period]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refetch]);

    const filterDataByPeriod = (data: TradeData[], period: string): TradeData[] => {
        let filteredDate: moment.Moment;
        const currentDate = moment();

        switch (period) {
            case "1_month":
                filteredDate = moment(currentDate).subtract(1, "month");
                break;
            case "3_months":
                filteredDate = moment(currentDate).subtract(3, "months");
                break;
            case "1_year":
                filteredDate = moment(currentDate).subtract(1, "year");
                break;
            case "2_years":
                filteredDate = moment(currentDate).subtract(2, "years");
                break;
            case "3_years":
                filteredDate = moment(currentDate).subtract(3, "years");
                break;
            default:
                return data;
        }

        return data.filter((item) => {
            const tradeDate = moment(item.tradeDate, "MM/DD/YYYY");
            return tradeDate.isAfter(filteredDate);
        });
    };

    return { filteredData, loading, error, refetch: fetchData };
};

export default useUnlistedGraphData;
